import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { gameDepartmentAPI } from '../../services/api';
import { cumulativeShare, mergeCurves, stickinessScore, emptyCurveMessage, tooNewGames } from './stickinessCurve';
import ReturnDaysSection from './ReturnDaysChart';

const AXIS_COLOR = '#7a8499';
const DEFAULT_WINDOW_DAYS = 60;

export const CURVE_METRICS = [
  { key: 'turnover', label: 'Turnover' },
  { key: 'n_bets',   label: 'Bets' },
];

function fmtPct(v) { return v == null ? '—' : `${Number(v).toFixed(1)}%`; }

function CurveTooltip({ active, payload, games }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-surface-2 border border-white/10 rounded-md p-2.5 text-[11px] max-w-[240px]">
      <p className="font-bold text-ink mb-1">Day {row.day}</p>
      {games.map(g => (
        <div key={g.key} className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
          {games.length > 1 && <span className="text-ink-dim truncate">{g.title}</span>}
          <span className="text-ink ml-auto pl-2">{fmtPct(row[g.key])}</span>
        </div>
      ))}
    </div>
  );
}

// Same 0-100 score as the Stickiness report's spend stickiness columns.
function StickinessSummary({ games, curves, metricLabel }) {
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-2 mb-3 text-[11px] text-ink-dim">
      {games.map(g => (
        <div key={g.key} className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
          <span className="text-ink font-bold">{g.title}</span>
          {curves[g.key] ? (
            <span>
              · {metricLabel.toLowerCase()} stickiness{' '}
              <span className="text-ink font-bold">{stickinessScore(curves[g.key]).toFixed(1)}</span>
            </span>
          ) : <span>· no activity after day 0</span>}
        </div>
      ))}
    </div>
  );
}

// Cumulative share of each game's day 1..60 turnover/bets, by days since each
// player's first bet on it. A curve that climbs fast means players spend
// early and drift off; a straighter line means they keep coming back.
// showReturnDays adds per-game returning-players bar charts, by cohort window, below.
export default function GameStickinessCurve({ games, metric, showReturnDays = false }) {
  const [pointsByKey, setPointsByKey] = useState({});
  const [cohortByKey, setCohortByKey] = useState({});
  const [windowDays, setWindowDays] = useState(DEFAULT_WINDOW_DAYS);
  const [cohortCutoff, setCohortCutoff] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const gameIds = games.map(g => `${g.key}:${g.uniqueGameId}`).join(',');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all(games.map(g => gameDepartmentAPI
      .getStickinessCurve({ uniqueGameId: g.uniqueGameId })
      .then(res => [g.key, res.data])))
      .then(entries => {
        if (cancelled) return;
        setWindowDays(entries[0]?.[1]?.window_days || DEFAULT_WINDOW_DAYS);
        setCohortCutoff(entries[0]?.[1]?.cohort_cutoff || '');
        setPointsByKey(Object.fromEntries(entries.map(([key, data]) => [key, data?.points || []])));
        setCohortByKey(Object.fromEntries(entries.map(([key, data]) => [key, data?.cohort_players || 0])));
      })
      .catch(() => { if (!cancelled) setError('Failed to load the stickiness curve.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // gameIds captures the identity of `games`; the array itself is rebuilt each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameIds]);

  const curves = useMemo(() => Object.fromEntries(
    games.map(g => [g.key, pointsByKey[g.key] ? cumulativeShare(pointsByKey[g.key], metric, windowDays) : null]),
  ), [games, pointsByKey, metric, windowDays]);

  const chartData = useMemo(() => mergeCurves(curves, windowDays), [curves, windowDays]);
  const hasData = Object.values(curves).some(Boolean);
  const metricLabel = CURVE_METRICS.find(m => m.key === metric)?.label || metric;
  const tooNew = tooNewGames(games, cohortByKey);

  if (error) return <Alert variant="error" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>;
  if (loading) return <div className="flex justify-center py-10"><Spinner size="sm" /></div>;

  return (
    <>
      <p className="text-[11px] text-ink-dim mb-3">
        Cumulative {metricLabel.toLowerCase()}, as % of each game's day 1–{windowDays} total, by days since each
        player's first bet on the game (day 0 excluded). Steeper early = less sticky; stickiness is 100 when spend follows the diagonal, near 0 when it all lands on one day.
        {cohortCutoff && ` Only players whose first bet was before ${cohortCutoff} count, so everyone has a full ${windowDays} days of data.`}
      </p>
      {hasData && tooNew.length > 0 && (
        <p className="text-[11px] text-warning mb-2">
          Too new for the curve (first played less than {windowDays} days ago): {tooNew.map(g => g.title).join(', ')}
        </p>
      )}
      {hasData && <StickinessSummary games={games} curves={curves} metricLabel={metricLabel} />}
      <div className="h-[320px] mb-2">
        {!hasData ? (
          <div className="flex items-center justify-center h-full px-6 text-center text-xs text-ink-dim">
            {emptyCurveMessage(games, cohortByKey, windowDays, cohortCutoff)}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="day" stroke={AXIS_COLOR} fontSize={11} interval={7} />
              <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} tickFormatter={v => `${v}%`} stroke={AXIS_COLOR} fontSize={11} width={48} />
              <Tooltip content={<CurveTooltip games={games} />} cursor={{ stroke: 'rgba(255,255,255,0.15)' }} />
              {games.map(g => (
                <Line key={g.key} type="monotone" dataKey={g.key} name={g.title} stroke={g.color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
      {showReturnDays && <ReturnDaysSection games={games} pointsByKey={pointsByKey} cohortByKey={cohortByKey} />}
    </>
  );
}
